/*
 * Planner schedule provider scoping.
 *
 * The EHR appointment feed attributes every appointment to the rendering /
 * attending provider only (providerId + providerName). It carries no concept
 * of a resident, interpreter, scribe, or other staff member who is *also*
 * working that schedule. So a resident (who never appears as the provider on
 * any appointment) cannot be auto-matched to "their" patients.
 *
 * Two layers live here:
 *  1. Auto scoping — best-effort match of the signed-in user to their own
 *     appointments by EHR providerId, else by provider-name token overlap.
 *  2. Manual scoping — when auto scoping can't confidently claim any rows, the
 *     user explicitly picks which attending provider(s)' schedule to view
 *     (one or many). That choice is persisted per user so they don't re-pick
 *     every visit.
 *
 * Delete alongside the rest of the client-side planner schedule composition
 * once the backend returns a pre-scoped schedule.
 */

const NAME_NOISE = new Set([
  'dr',
  'doctor',
  'md',
  'do',
  'np',
  'pa',
  'rn',
  'lpn',
  'phd',
  'mbbs',
  'mr',
  'mrs',
  'ms',
  'miss',
  'prof',
  'professor',
  'jr',
  'sr',
  'ii',
  'iii',
  'iv',
]);

export function normalizeNameTokens(name: any): string[] {
  if (!name || typeof name !== 'string') return [];
  return name
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ')
    .split(' ')
    .filter(token => token.length >= 2 && !NAME_NOISE.has(token));
}

export function deriveProviderTokens(provider: any): string[] {
  if (!provider) return [];
  const fromName = normalizeNameTokens(
    provider.displayName || provider.providerName,
  );
  if (fromName.length >= 2) return fromName;
  const email = provider.email || '';
  if (email) {
    const local = String(email).split('@')[0] || '';
    const fromEmail = normalizeNameTokens(local.replace(/[._-]+/g, ' '));
    if (fromEmail.length >= 2) return fromEmail;
  }
  return fromName;
}

function providerNameMatchesTokens(appt: any, providerTokens: any[]): boolean {
  if (providerTokens.length < 2) return false;
  const apptTokens = new Set(normalizeNameTokens(appt?.providerName));
  if (!apptTokens.size) return false;
  let overlap = 0;
  for (const token of providerTokens) {
    if (apptTokens.has(token)) overlap += 1;
    if (overlap >= 2) return true;
  }
  return false;
}

export function appointmentMatchesProvider(
  appt: any,
  providerId: any,
  providerTokens: any[],
  provider: any = {},
): boolean {
  if (!appt) return false;
  const hasProviderNameSignal = providerTokens.length >= 2;
  const providerNameMatches = providerNameMatchesTokens(appt, providerTokens);
  if (providerId) {
    // Attendings match the billing providerId; residents/staff match the
    // rendering resource id(s). Either makes the appointment theirs.
    if (appt.providerId != null && providerId === String(appt.providerId)) {
      return !hasProviderNameSignal || providerNameMatches;
    }
    const resourceIds = Array.isArray(appt.resourceIds) ? appt.resourceIds : [];
    if (resourceIds.some((id: any) => String(id) === providerId)) {
      return provider?.ehrIsResident === true || providerNameMatches;
    }
  }
  return providerNameMatches;
}

// Stable identity for an appointment's provider: prefer the EHR providerId,
// fall back to the normalized provider name. Used both to group the picker
// list and to test membership against a saved selection.
export function appointmentProviderKey(appt: any): string | null {
  const id = appt?.providerId;
  if (id != null && String(id).trim()) return `id:${String(id).trim()}`;
  const tokens = normalizeNameTokens(appt?.providerName);
  if (tokens.length) return `name:${tokens.join(' ')}`;
  return null;
}

export interface ScheduleProviderOption {
  key: string;
  name: string;
  count: number;
}

// Distinct attending providers on a set of appointments, with appointment
// counts, sorted by name. Rows with no usable provider name are skipped (they
// can't be picked).
export function listAppointmentProviders(
  appointments: any[] = [],
): ScheduleProviderOption[] {
  const byKey = new Map<string, ScheduleProviderOption>();
  for (const appt of appointments) {
    const key = appointmentProviderKey(appt);
    const name = String(appt?.providerName || '').trim();
    if (!key || !name) continue;
    const existing = byKey.get(key);
    if (existing) existing.count += 1;
    else byKey.set(key, {key, name, count: 1});
  }
  return [...byKey.values()].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, {sensitivity: 'base'}),
  );
}

export interface ScheduleScopeSelection {
  all: boolean;
  keys: string[];
}

export const isSelectionActive = (
  selection: ScheduleScopeSelection | null,
): boolean =>
  Boolean(selection && (selection.all || selection.keys.length > 0));

// Best-effort auto match of the signed-in user to their own appointments.
// Returns matched=false when we have nothing to match on (no providerId and a
// too-thin name) OR when a match was attempted but claimed zero rows.
export function autoScopeAppointments(
  appointments: any[] = [],
  provider: any,
): {matched: boolean; appointments: any[]} {
  if (!appointments.length || !provider) {
    return {matched: false, appointments: []};
  }
  const providerId =
    provider.providerId != null ? String(provider.providerId) : null;
  const providerTokens = deriveProviderTokens(provider);
  if (!providerId && providerTokens.length < 2) {
    return {matched: false, appointments: []};
  }
  const mine = appointments.filter(appt =>
    appointmentMatchesProvider(appt, providerId, providerTokens, provider),
  );
  return {matched: mine.length > 0, appointments: mine};
}

export function filterAppointmentsBySelection(
  appointments: any[] = [],
  selection: ScheduleScopeSelection | null,
): any[] {
  if (!selection) return [];
  if (selection.all) return appointments;
  if (!selection.keys.length) return [];
  const keys = new Set(selection.keys);
  return appointments.filter(appt => {
    const key = appointmentProviderKey(appt);
    return key != null && keys.has(key);
  });
}

// ── Persistence (per user, survives reloads) ─────────────────────
const STORAGE_PREFIX = 'planner.scheduleScope.v1';

const storageKey = (userId: any) =>
  userId ? `${STORAGE_PREFIX}.${userId}` : null;

export function loadStoredSelection(
  userId: any,
): ScheduleScopeSelection | null {
  const key = storageKey(userId);
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const keys = Array.isArray(parsed.keys)
      ? parsed.keys.filter((k: any) => typeof k === 'string')
      : [];
    return {all: Boolean(parsed.all), keys};
  } catch {
    return null;
  }
}

export function storeSelection(
  userId: any,
  selection: ScheduleScopeSelection | null,
): void {
  const key = storageKey(userId);
  if (!key || typeof window === 'undefined') return;
  try {
    if (!isSelectionActive(selection)) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(selection));
  } catch {
    /* best-effort persistence */
  }
}
